import { useMemo } from 'react';
import {
  concatenateConditionOptions,
  dateTimeTypeParameterOptions,
  dateTypeParameterOptions,
  depreciationResultOptions,
  ruleConditionOptions,
  staticColumnParameterDropdownOption,
  staticTableParameterDropdownOption,
} from 'data/Dropdown';
import { SUBJECT_TYPE } from 'constant/DataInputConstant';
import { useAttributeDropdown } from 'services/v1/SystemTenant/AROKMS/AttributeService';
import { useGetKTreeDefinitionListDropdown } from 'services/v1/SystemTenant/AROKMS/KTreeBuilderService';
import {
  useGetKTreeInferenceOptions,
  useGetLimitedDropdownAssociteSubject,
} from 'services/v1/SystemTenant/AROKMS/TableDefinitionService';
import { useSubjectDropdown, useSubjectUsedDropdown } from 'services/v1/SystemTenant/AROKMS/SubjectService';
import { RuleTypeItem } from 'types/api/SystemTenant/AROKMS/TableDefinitionTypes';
import { RuleParameterOption, RuleParameterValues } from '../types';

function optionValue(value: unknown) {
  return value && typeof value === 'object' && 'value' in value
    ? (value as RuleParameterOption).value
    : undefined;
}

function normalizeOptions(options: Array<Partial<RuleParameterOption>> = []): RuleParameterOption[] {
  return options
    .filter((option) => option.value !== undefined && option.label !== undefined)
    .map((option) => ({
      ...option,
      label: String(option.label),
      value: option.value as string | number,
      persistentName: option.persistentName || String(option.label),
    }));
}

export function useRuleParameterOptions({
  subjectId,
  ruleType,
  currentAttributeLabel,
  associatedSubjectLabel,
  associatedSubjectId,
  inferenceKTreeDefinitionId,
  parameters,
}: {
  subjectId: string | number;
  ruleType: RuleTypeItem | null;
  currentAttributeLabel?: string;
  associatedSubjectLabel?: string;
  associatedSubjectId?: string | number;
  inferenceKTreeDefinitionId?: string | number;
  parameters: RuleParameterValues;
}) {
  const dependentSubjectId = optionValue(parameters.SUBJECT);
  const staticTable = optionValue(parameters.STABLE) || '';
  const currentLevelValue = parameters.CURRENT_LEVEL;
  const currentLevel = currentLevelValue === '' || currentLevelValue === undefined ? undefined : Number(currentLevelValue);

  const attributeFilter = {
    tableDefinitionSubjectId: subjectId,
    dataTypeId: undefined,
    ruleTypeId: undefined,
    dependenciesAttributeId: ruleType?.allowedDependenciesDataTypes || undefined,
  };
  const { data: currentAttributes, isLoading: isLoadingCurrentAttributes } = useAttributeDropdown(attributeFilter);
  const { data: dependentAttributes, isLoading: isLoadingDependentAttributes } = useAttributeDropdown({
    tableDefinitionSubjectId: dependentSubjectId,
    dataTypeId: undefined,
  });
  const { data: usedSubjects, isLoading: isLoadingUsedSubjects } = useSubjectUsedDropdown({
    enabled: true,
    subjectType: SUBJECT_TYPE.TABLE,
  });
  const { data: allSubjects, isLoading: isLoadingAllSubjects } = useSubjectDropdown(undefined, SUBJECT_TYPE.TABLE);
  const { data: limitedSubjects, isLoading: isLoadingLimitedSubjects } =
    useGetLimitedDropdownAssociteSubject(subjectId);
  const { data: kTrees, isLoading: isLoadingKTrees } = useGetKTreeDefinitionListDropdown(dependentSubjectId);
  const {
    data: inferenceOptions,
    isLoading: isLoadingInferenceOptions,
    isError: isInferenceOptionsError,
    error: inferenceOptionsError,
  } = useGetKTreeInferenceOptions(
    {
      kTreeDefinitionId: inferenceKTreeDefinitionId,
      currentLevel: Number.isInteger(currentLevel) ? currentLevel : undefined,
      associatedSubjectId,
    },
    Boolean(inferenceKTreeDefinitionId && associatedSubjectId)
  );

  const optionsBySource = useMemo<Record<string, RuleParameterOption[]>>(() => {
    const currentAttributeOptions = normalizeOptions(currentAttributes?.data).filter(
      (option) => option.label !== currentAttributeLabel
    );
    const limitedSubjectOptions = normalizeOptions(limitedSubjects?.data).filter(
      (option) => option.label !== associatedSubjectLabel
    );
    const subjectOptions = normalizeOptions(usedSubjects?.data);
    const allSubjectOptions = normalizeOptions(
      allSubjects?.data.map((subject) => ({
        label: subject.subject,
        value: subject.id,
        persistentName: subject.subject,
      }))
    );
    const staticColumns =
      staticColumnParameterDropdownOption[staticTable as keyof typeof staticColumnParameterDropdownOption] || [];

    return {
      ATTRIBUTE_DROPDOWN: currentAttributeOptions,
      ATTRIBUTE_DROPDOWN_DEPENDANT: normalizeOptions(dependentAttributes?.data),
      SUBJECT_DROPDOWN: subjectOptions,
      SUBJECT_DROPDOWN_ALL: allSubjectOptions,
      LIMITED_SUBJECT_LIST_DROPDOWN: limitedSubjectOptions,
      KTREE_DROPDOWN: normalizeOptions(kTrees?.data),
      KTREE_PARENT_NODE_ATTRIBUTE_DROPDOWN: normalizeOptions(inferenceOptions?.data.parentNodeAttributes),
      KTREE_PARENT_SOURCE_ATTRIBUTE_DROPDOWN: normalizeOptions(inferenceOptions?.data.sourceAttributes),
      DATE_TYPE_DROPDOWN: normalizeOptions(dateTypeParameterOptions),
      DATETIME_TYPE_DROPDOWN: normalizeOptions(dateTimeTypeParameterOptions),
      STATIC_TABLE_DROPDONW: normalizeOptions(staticTableParameterDropdownOption),
      STATIC_COLUMN_DROPDOWN: normalizeOptions(staticColumns),
      F_DEPRECIATION_RESULT: normalizeOptions(depreciationResultOptions),
      RULE_CONDITION_TYPES_OPTIONS: normalizeOptions(ruleConditionOptions),
      CONCATE_RULES_OPTIONS: normalizeOptions(concatenateConditionOptions),
    };
  }, [
    allSubjects?.data,
    associatedSubjectLabel,
    currentAttributeLabel,
    currentAttributes?.data,
    dependentAttributes?.data,
    inferenceOptions?.data,
    kTrees?.data,
    limitedSubjects?.data,
    staticTable,
    usedSubjects?.data,
  ]);

  const loadingBySource: Record<string, boolean> = {
    ATTRIBUTE_DROPDOWN: isLoadingCurrentAttributes,
    ATTRIBUTE_DROPDOWN_DEPENDANT: isLoadingDependentAttributes,
    SUBJECT_DROPDOWN: isLoadingUsedSubjects,
    SUBJECT_DROPDOWN_ALL: isLoadingAllSubjects,
    LIMITED_SUBJECT_LIST_DROPDOWN: isLoadingLimitedSubjects,
    KTREE_DROPDOWN: isLoadingKTrees,
    KTREE_PARENT_NODE_ATTRIBUTE_DROPDOWN: isLoadingInferenceOptions,
    KTREE_PARENT_SOURCE_ATTRIBUTE_DROPDOWN: isLoadingInferenceOptions,
  };

  return {
    getOptions: (source?: string | null) => (source ? optionsBySource[source] || [] : []),
    isLoading: (source?: string | null) => Boolean(source && loadingBySource[source]),
    isSupportedSource: (source?: string | null) => !source || Object.prototype.hasOwnProperty.call(optionsBySource, source),
    isInferenceOptionsError,
    inferenceOptionsError,
  };
}
